import { isValid, isWithinInterval, roundToNearestMinutes } from "date-fns";
import { debounce } from "../utils/Performance";

export default class Listing {
    constructor(props) {
        this.props = props;
        this.el = props.root;
        this.id = this.el.id;
        this.ssrMode = this.el.hasAttribute("data-ssr-mode") || false;

        this.loadingIndicator = document.getElementById(
            `${this.id}-loading-indicator`,
        );

        this.form = props.form;
        this.containerId = props.listingId || "listing-items";
        this.callback = props.callback;
        this.daterange = props.daterange || "";
        this.jsonDataId = props.jsonDataId || "json-data";
        this.cardId = props.cardId || "[data-id]";
        this.pagination = props.pagination || false;
        this.promoInterval = props.promoInterval || 3;
        this.promoItemTemplate = document.querySelector("[data-promo-items]");
        this.promoItems = this.getPromoItems();
        this.objectName = props.objectName || {
            singular: "result",
            plural: "results",
        };

        if (this.form) {
            this.liveSubmit = this.form.dataset.live === "true";
        }

        // Initialize state
        this.state = {
            noResults: false,
            isFiltered: false,
            query: {},
            filtersStatus: {},
            filtered: [],
        };

        this.loadContent(props);
    }

    async loadContent(props) {
        await this.fetchContent(props);

        this.initializeElements();

        if (this.form) {
            this.loadQueryFromUrl();
        }

        this.init();
    }

    async fetchContent(props) {
        if (this.ssrMode) {
            console.log("[Listing]: Dev Mode (do not fetch data)");

            this.data = document.getElementById(this.jsonDataId).innerHTML;
            this.data = JSON.parse(this.data);
        } else {
            let cache_data_lifespan = roundToNearestMinutes(new Date(), {
                nearestTo: 15,
                roundingMethod: "ceil",
            });

            if (props.html) {
                try {
                    const htmlResponse = await fetch(
                        `${props.html}?t=${cache_data_lifespan.getTime()}`,
                        { redirect: "error" },
                    );

                    if (htmlResponse.ok) {
                        const htmlResponseText = await htmlResponse.text();

                        this.el.innerHTML = htmlResponseText;
                    } else {
                        console.error(
                            "Failed to fetch HTML content",
                            htmlResponse.status,
                        );
                    }
                } catch (error) {
                    console.error("Error fetching HTML content", error);

                    if (this.loadingIndicator) {
                        this.loadingIndicator.setAttribute("active", false);
                    }

                    return;
                }
            }

            if (props.json) {
                try {
                    const jsonResponse = await fetch(
                        `${props.json}?t=${cache_data_lifespan.getTime()}`,
                        { redirect: "error" },
                    );

                    if (jsonResponse.ok) {
                        this.data = await jsonResponse.json();
                    } else {
                        console.error(
                            "Failed to fetch JSON content",
                            jsonResponse.status,
                        );
                    }
                } catch (error) {
                    console.error("Error fetching JSON content", error);
                }

                if (!this.data) {
                    if (this.loadingIndicator) {
                        this.loadingIndicator.setAttribute("active", false);
                    }

                    return;
                }
            }
        }

        this.container = document.getElementById(this.containerId);

        if (this.loadingIndicator) {
            this.loadingIndicator.setAttribute("active", false);
        }
    }

    initializeElements() {
        this.listingInnerEl = this.el.querySelector(".listing__inner");
        this.noResultsEl = document.querySelector("[data-no-results]");
        this.statusEls = document.querySelectorAll("[data-listing-status]");

        // The filters form
        if (this.form) {
            this.formInputs = document.querySelectorAll(
                `#${this.form.id} input, [form="${this.form.id}"]`,
            );

            this.resetButtons = document.querySelectorAll(
                `[type="reset"][form="${this.form.id}"]`,
            );
        }

        if (this.pagination) {
            this.paginationEl = document.querySelector(this.pagination.el);
        }

        // Make listing element visible
        this.listingInnerEl.hidden = false;
    }

    init() {
        if (!this.container) return;

        // Filtered items placeholder
        this.filtered = [];

        // SSR HTML card items
        this.items = this.container.querySelectorAll(this.cardId);

        this.featuredEl = this.el.querySelector(".listing__featured");

        if (this.form) {
            // Callback bound to this class and debounced
            const debouncedFilterItems = debounce(
                this.filterItems.bind(this),
                250,
            );

            // Trigger a change event upon resetting the form
            // Resets the listing content to its original state
            this.form.addEventListener("reset", (e) => {
                console.log("[Listing]: Reset form");

                setTimeout(() => {
                    this.filterItems();
                }, 50);
            });

            // Attach change event to form
            const event = this.liveSubmit ? "input" : "submit";

            // Attach change event to inputs linked to the form
            this.formInputs.forEach((input) => {
                input.addEventListener(event, (e) => {
                    debouncedFilterItems();
                });
            });

            this.handleFormResetButtons();
        }

        if (this.pagination) {
            this.paginate();
        }

        if (this.form) {
            this.filterItems();
        } else {
            this.render();
        }
    }

    handleFormResetButtons() {
        this.resetButtons.forEach((item) => {
            item.addEventListener("click", (e) => {
                e.preventDefault();

                this.form.reset();
                this.form.dispatchEvent(new Event("change"));
            });
        });
    }

    toggleLinkedElements() {
        const isFiltered = this.state.isFiltered;

        const toggleVisibility = (nodeList, hide) => {
            nodeList.forEach((element) => {
                element.hidden = hide;
            });
        };

        const hideElements = document.querySelectorAll(
            `[data-hide-filtered="${this.el.id}"], #${this.el.id} [data-hide-filtered]`,
        );

        toggleVisibility(hideElements, isFiltered);

        const showElements = document.querySelectorAll(
            `[data-show-filtered="${this.el.id}"], #${this.el.id} [data-show-filtered]`,
        );

        toggleVisibility(showElements, !isFiltered);
    }

    getVisibleItems() {
        if (this.state.isFiltered) {
            return this.container.querySelectorAll(`:scope>li`);
        } else {
            return this.container.querySelectorAll(
                `:scope>li:not([data-featured])`,
            );
        }
    }

    getPromoItems() {
        if (!this.promoItemTemplate) return [];

        const promoItems =
            this.promoItemTemplate.content.querySelectorAll("li");

        return [...promoItems];
    }

    insertPromoItems(items) {
        if (!this.promoItems.length) return items;

        let result = [];
        let promoIndex = 0;

        items.forEach((item, index) => {
            // Insert promo after every nth item
            if (index % this.promoInterval === 0 && index !== 0) {
                const promoItem =
                    this.promoItems[promoIndex % this.promoItems.length];
                result.push(promoItem.cloneNode(true));

                promoIndex++;
            }

            result.push(item);
        });

        return result;
    }

    paginate() {
        const items = this.getVisibleItems();
        const totalItems = items.length;
        const perPage = this.pagination.perPage;

        console.log({
            totalItems,
            perPage,
        });

        [...items].map((item, index) => {
            let status = index >= perPage;
            item.setAttribute("aria-hidden", status);
            return item;
        });

        this.paginationEl.hidden = totalItems <= perPage;

        let button = this.paginationEl.querySelector("button");

        // Remove existing event listeners
        button.replaceWith(button.cloneNode(true));
        button = this.paginationEl.querySelector("button");

        button.addEventListener("click", (e) => {
            let hiddenCards = this.container.querySelectorAll(
                '[aria-hidden="true"]',
            );

            [...hiddenCards].slice(0, perPage).forEach((item) => {
                item.setAttribute("aria-hidden", "false");
            });

            hiddenCards = this.container.querySelectorAll(
                '[aria-hidden="true"]',
            );
            this.paginationEl.hidden = hiddenCards.length <= 0;
        });
    }

    updateStatus() {
        if (!this.statusEls.length) {
            return;
        }

        if (this.state.noResults) {
            this.statusEls.forEach((el) => {
                el.innerHTML = "No results, please try again";
            });

            return;
        }

        if (!this.state.isFiltered) {
            this.statusEls.forEach((el) => {
                el.innerHTML = "";
            });

            return;
        }

        let count = this.state.filtered.length;

        if (this.state.filtersStatus.s) {
            let pluralise = count > 1 ? "results" : "result";

            this.statusEls.forEach((el) => {
                el.innerHTML = `Found ${count} ${pluralise} for ‘${this.state.filtersStatus.s}’`;
            });

            return;
        }

        let objectName =
            count > 1 ? this.objectName.plural : this.objectName.singular;

        this.statusEls.forEach((el) => {
            el.innerHTML = `Found ${count} ${objectName}`;
        });
        return;
    }

    updateUrl() {
        const query = new URLSearchParams(this.state.query).toString();
        const newUrl = query
            ? `${window.location.pathname}?${query}`
            : window.location.pathname;
        window.history.replaceState({}, "", newUrl);
    }

    loadQueryFromUrl() {
        const urlParams = new URLSearchParams(window.location.search);
        const query = {};

        urlParams.forEach((value, key) => {
            if (!query[key]) {
                query[key] = [];
            }

            value.split(",").forEach((val) => {
                query[key].push(val);
            });
        });

        if (!Object.keys(query).length) {
            return;
        }

        this.state.query = query;

        Object.keys(query).forEach((key) => {
            const inputs = document.querySelectorAll(
                `#${this.form.id} [name="${key}"],
                [form="${this.form.id}"][name="${key}"]`,
            );

            inputs.forEach((input) => {
                if (input.type === "checkbox" || input.type === "radio") {
                    query[key].forEach((value) => {
                        if (input.value === value) {
                            input.checked = true;
                        }
                    });
                } else if (input.type === "text") {
                    input.value = query[key][0];

                    input.dispatchEvent(new Event("input"), {
                        bubbles: true,
                    });
                }
            });
        });
    }

    filterItems(e) {
        // Get current form data
        let form_data = new FormData(this.form);

        // Build query dictionary
        let query = {};

        for (const pair of form_data.entries()) {
            if (pair[1] && pair[1] != "undefined") {
                query[pair[0]] = form_data.getAll(pair[0]);
            }
        }

        this.state.query = query;

        if (!Object.values(query).length) {
            // Set state
            this.state.noResults = false;
            this.state.isFiltered = false;
            this.state.filtersStatus = {};
        } else {
            // Filter JSON data
            this.state.filtered = this.data.filter((item) => {
                let match = [];

                Object.keys(query).forEach((name) => {
                    // Placeholder for matching nodes
                    let matching = new Set();
                    let isArray = Array.isArray(item[name]) ? true : false;

                    this.state.filtersStatus[name] = {};

                    if (!item[name]) {
                        return;
                    }

                    if (this.daterange == name) {
                        // Date range field

                        // Split date range into start, end
                        let [start, end] = query[name].join("").split(" - ");

                        if (!end) {
                            end = start;
                        }

                        this.state.filtersStatus[name] = [start, end];

                        // Find a match
                        let dateMatches = item[name].filter((date) => {
                            return isWithinInterval(date, {
                                start: start,
                                end: end,
                            });
                        });

                        let search = dateMatches.length ? true : false;

                        match.push(search);
                    } else if (name === "s") {
                        let search_value = query[name][0].toLowerCase();
                        let search = item.s
                            .toLowerCase()
                            .includes(search_value);

                        this.state.filtersStatus[name] = query[name][0];

                        match.push(search);
                    } else {
                        // Default matching
                        let input_value = query[name];

                        this.state.filtersStatus[name] = input_value;

                        // Data provided by third party might be separated by a comma
                        // Let's split that into an Array
                        if (input_value[0].indexOf(",") >= 0) {
                            input_value = query[name].split(",");
                        }

                        // Create the search Set from query data
                        let search = new Set(input_value);

                        // Create the matching set from JSON data
                        if (isArray) {
                            matching = new Set(item[name]);
                        } else {
                            matching.add(item[name]);
                        }

                        // Find data intersaction
                        let intersection = new Set(
                            [...matching].filter((x) => search.has(x)),
                        );

                        intersection = intersection.size ? true : false;
                        match.push(intersection);

                        // Unsupported in Safari 16 - core-js update?
                        // match.push(!matching.isDisjointFrom(search));
                    }
                });

                // Return matching nodes only
                return !match.includes(false);
            });

            // Sort by date
            let sorted = this.state.filtered.sort(
                (a, b) => new Date(a[this.date]) - new Date(b[this.date]),
            );

            // Pluck IDs
            this.state.filtered = sorted.map((item) => item.id);

            // Set state
            this.state.noResults = this.state.filtered.length ? false : true;
            this.state.isFiltered = true;
        }

        // Render filtered items
        this.render();

        // Eventually run the attached callback
        if (this.callback) {
            this.callback();
        }

        // Prevent default form submission
        return false;
    }

    getItems() {
        if (!this.state.isFiltered) {
            // If no filters are applied, return all items except featured items
            return [...this.items].filter((item) => {
                return !item.hasAttribute("data-featured");
            });
        }

        return [...this.items]
            .sort((a, b) => {
                return (
                    this.state.filtered.indexOf(a.dataset.id) -
                    this.state.filtered.indexOf(b.dataset.id)
                );
            })
            .filter((item) => {
                if (item.hasAttribute("data-featured")) {
                    item.hidden = false;
                }
                return this.state.filtered.includes(item.dataset.id);
            });
    }

    render() {
        console.log("[Listing]: Render", this.state);

        this.updateUrl();

        this.container.innerHTML = "";
        this.container.classList.toggle("filtered", this.state.isFiltered);

        let items = this.getItems();

        // Insert promo items if available
        if (this.promoItems.length) {
            items = this.insertPromoItems(items);
        }

        // Append all items to container
        items.forEach((item) => {
            this.container.append(item);
        });

        // Pagination
        if (this.pagination) {
            this.paginate(this.pagination.perPage);
        }

        this.noResultsEl && (this.noResultsEl.hidden = !this.state.noResults);

        this.toggleLinkedElements();
        this.updateStatus();
    }
}
